import { ApolloClient } from "@apollo/client";
import { GET_RECENT_APPS } from "../api/graphql/applications";
import { GET_MY_ACCOUNTS } from "../api/graphql/team";

/**
 * The account to open when this browser holds no choice — a new device,
 * another browser, site data cleared.
 *
 * Without a choice everyone starts in their own account. That is right for an
 * owner and wrong for a teammate: their own account is usually empty, so every
 * screen in it asks them to add an app, while the apps they work on sit in the
 * team's account behind the switcher.
 *
 * So the own account stays when it has apps or the person is in no team;
 * otherwise the first team account, in the order they joined, that has apps.
 * null keeps the own account.
 */
export const pickDefaultAccount = async (
  client: ApolloClient<unknown>,
): Promise<number | null> => {
  const { data } = await client.query({
    query: GET_MY_ACCOUNTS,
    fetchPolicy: "no-cache",
  });
  const teams: { id: number }[] = (data?.myAccounts ?? []).filter(
    (account: { isOwn: boolean }) => !account.isOwn,
  );
  if (!teams.length) return null;

  // No account is stored, so accountLink adds no header and this one passes.
  const hasApps = async (accountId?: number) => {
    const { data } = await client.query({
      query: GET_RECENT_APPS,
      fetchPolicy: "no-cache",
      context: {
        // The same query for different accounts: never merge two in flight.
        queryDeduplication: false,
        ...(accountId && { headers: { "x-account-id": String(accountId) } }),
      },
    });
    return Boolean(data?.getRecentApplications?.length);
  };

  if (await hasApps()) return null;
  for (const team of teams) {
    if (await hasApps(team.id)) return team.id;
  }
  return null;
};
